/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';
import styled, { css as cssHelper } from 'styled-components';

import Colors from 'constants/Colors';
import { Text, FontWeight } from '../../Text';
import Icon from '../../Icon';

const propTypes = {
  label: PropTypes.string,
  option: PropTypes.any.isRequired,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func
};

const defaultProps = {
  label: null,
  tabIndex: 0,
  onChange: value => value
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  user-select: none;
  overflow: hidden;

  &:focus {
    outline: none;
  }
`;

const Label = styled(Text).attrs({
  weight: FontWeight.Bold
})`
  font-size: 16px;
  color: ${Colors.GRAY[2]};
  overflow: hidden;
  white-space: nowrap;
  line-height: 150%;
  text-overflow: ellipsis;
  margin-left: 8px;

  ${({ active }) =>
    active
      ? cssHelper`
    color: ${Colors.GRAY[0]};
  `
      : ''}
`;

const FontIcon = styled(({ isOpen, ...props }) => <Icon {...props} />)`
  color: ${Colors.GRAY[2]};
  font-size: 26px;

  ${({ checked }) =>
    checked &&
    cssHelper`
    color: ${Colors.GRAY[0]};
  `}
`;

const RadioButton = ({
  label,
  option,
  tabIndex,
  onChange,
  disabled,
  field,
  form: { setFieldValue, setFieldTouched, setStatus, status, touched, errors },
  ...props
}) => {
  const meta = {
    isFocused: status && status[field.name] && status[field.name].isFocused,
    touched: touched[field.name],
    error: errors[field.name],
    disabled
  };

  const handleStateChange = useCallback((newState = {}) => ({
    ...status,
    [field.name]: {
      ...(status ? status[field.name] : {}),
      ...newState
    }
  }));

  const toogleField = () => {
    if (!disabled) {
      setFieldValue(field.name, option);
      onChange(option, meta);
    }
  };

  return (
    <Container
      name={field.name}
      onClick={toogleField}
      onKeyPress={e => {
        if (e.key.toLowerCase() === 'enter' && !disabled) {
          toogleField(e);
        }
      }}
      onFocus={() => {
        setStatus(
          handleStateChange({
            isFocused: true
          })
        );
      }}
      onBlur={() => {
        setFieldTouched(field.name, true, true);
        setStatus(
          handleStateChange({
            isFocused: false
          })
        );
      }}
      role="button"
      tabIndex={tabIndex}
      disabled={disabled}
      {...props}
    >
      {JSON.stringify(field.value) === JSON.stringify(option) ? (
        <FontIcon name="radio-checked" checked />
      ) : (
        <FontIcon name="radio" />
      )}
      {label && (
        <Label
          weight={FontWeight.Bold}
          active={JSON.stringify(field.value) === JSON.stringify(option)}
        >
          {label}
        </Label>
      )}
    </Container>
  );
};

RadioButton.propTypes = propTypes;
RadioButton.defaultProps = defaultProps;

export default RadioButton;
